import type { OverridedMixpanel } from '#mixpanel';
import { Alert } from '@oncarrot/turnip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AbilityProvider } from 'context/AbilityProvider/AbilityProvider';
import { MixpanelProvider } from 'context/MixpanelProvider/MixpanelProvider';
import { useAccount } from 'hooks/useAccount.am';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useJwt } from 'hooks/useJwt';
import { useTeamMemberAbilities } from 'hooks/useTeamMemberAbilities.am';
import { ErrorPage } from 'pages/ErrorPage/ErrorPage';
import { posthog } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { ScrollRestoration, useLocation, useNavigate } from 'react-router';
import { CenteredPageLoadingCarrot } from 'turnip/LoadingCarrot/LoadingCarrot';
import { Offset } from 'turnip/Offset/Offset';
import { PageLayoutContained } from 'turnip/PageLayoutContained/PageLayoutContained';
import { PageTitle } from 'turnip/PageTitle/PageTitle';
import { SupportLink } from 'turnip/SupportLink/SupportLink';
import { ThirdPartyScriptLoader } from './ThirdPartyScriptLoader/ThirdPartyScriptLoader';
import { useHubSpotUser } from './useHubSpotUser';
import { usePostHogUser } from './usePostHogUser';
import { useSentryUser } from './useSentryUser';

// Initialize PostHog
posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
  api_host: 'https://us.i.posthog.com',
  capture_pageview: false,
});

export type RootProps = {
  queryClient: QueryClient;
  mixpanel?: OverridedMixpanel;
  googleAnalyticsId: string;
  hubSpotId: string;
};

export function Root({
  queryClient,
  mixpanel,
  googleAnalyticsId,
  hubSpotId,
  children,
}: RootProps & { children: React.ReactNode }) {
  // Initialize carrot global namespace; we occasionally stick something useful
  // for tests in here
  useEffect(() => {
    window.carrot = window.carrot ?? {};
  }, []);

  useUntrailingSlashPath();

  return (
    <QueryClientProvider client={queryClient}>
      <PostHogProvider client={posthog}>
        <MixpanelProvider mixpanel={mixpanel}>
          <UserLoader>
            <ThirdPartyScriptLoader
              googleAnalyticsId={googleAnalyticsId}
              hubSpotId={hubSpotId}
            />

            {children}
          </UserLoader>
        </MixpanelProvider>
      </PostHogProvider>

      <ScrollRestoration />
      <MaybeReactQueryDevtools />
    </QueryClientProvider>
  );
}

function UserLoader({ children }: { children: React.ReactNode }) {
  const jwtQuery = useJwt();
  const userQuery = useCurrentUser();

  useHubSpotUser();
  useSentryUser();
  usePostHogUser();

  if (jwtQuery.isPending || userQuery.isLoading) {
    return <CenteredPageLoadingCarrot title="Loading user data" />;
  }

  if (userQuery.isError) {
    return (
      <Offset top={24}>
        <PageLayoutContained>
          <PageTitle>Something went wrong</PageTitle>

          <Offset top={4}>
            <Alert headline="An error occurred" variant="error">
              Something went wrong while processing your request. Please try
              again or <SupportLink />.
            </Alert>
          </Offset>
        </PageLayoutContained>
      </Offset>
    );
  }

  return <ContingentUserDataLoader>{children}</ContingentUserDataLoader>;
}

// These queries depend on the user query and can cause retry loops if they are
// rendered at the same level as it.
// TODO: ALUM - when these no longer depend on the user query, we may be able
// to lift them back up. Be sure to raise errors on the backend to test the
// behavior.
function ContingentUserDataLoader({ children }: { children: React.ReactNode }) {
  const accountQuery = useAccount();
  const abilitiesQuery = useTeamMemberAbilities();

  if (accountQuery.isLoading || abilitiesQuery.isLoading) {
    return <CenteredPageLoadingCarrot title="Loading account data" />;
  }

  if (accountQuery.isError || abilitiesQuery.isError) {
    return <ErrorPage />;
  }

  return <AbilityProvider>{children}</AbilityProvider>;
}

/**
 * Remove the trailing slash from the requested path, if present.
 *
 * Normalized AMUI URLs don't have the trailing slash, but occasionally
 * someplace else links to AMUI with one, and in very rare edge cases it can
 * cause a problem.
 */
function useUntrailingSlashPath() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === '/' || !location.pathname.endsWith('/')) {
      return;
    }

    navigate(location.pathname.slice(0, -1) + location.search + location.hash, {
      replace: true,
    });
  }, [location, navigate]);
}

function MaybeReactQueryDevtools() {
  // Don't load the TanStack Query dev tools if the page is being loaded by
  // Playwright in our tests
  if (window.navigator.webdriver) {
    return null;
  }

  return <ReactQueryDevtools initialIsOpen={false} />;
}
